import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, DollarSign, Calendar } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { CurrencyRate, Currency } from '../types';
import { supabase } from '../lib/supabase';
import { useTenant } from '../contexts/TenantContext';

const CurrencyRatesList: React.FC = () => {
  const { currencyRates, addCurrencyRate, updateCurrencyRate, deleteCurrencyRate } = useData();
  const { currentCompany } = useTenant();
  const [showForm, setShowForm] = useState(false);
  const [editingRate, setEditingRate] = useState<CurrencyRate | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [currencies, setCurrencies] = useState<Currency[]>([]);

  const [formData, setFormData] = useState({
    currency: 'USD' as 'USD' | 'EUR' | 'CLF',
    rate: '',
    date: ''
  });

  // Filter currency rates by current company
  const companyCurrencyRates = currencyRates.filter(rate => rate.company_id === currentCompany?.id);

  useEffect(() => {
    loadCurrencies();
  }, []);

  const loadCurrencies = async () => {
    try {
      const { data, error } = await supabase
        .from('currencies')
        .select('*')
        .in('code', ['USD', 'EUR', 'CLF'])
        .eq('is_active', true)
        .order('code');

      if (error) throw error;
      setCurrencies(data || []);
    } catch (error) {
      console.error('Error loading currencies:', error);
    }
  };

  const filteredRates = companyCurrencyRates.filter(rate => {
    const matchesSearch = rate.currency.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCurrency = !currencyFilter || rate.currency === currencyFilter;
    return matchesSearch && matchesCurrency;
  }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const rateData = {
      currency: formData.currency,
      rate: parseFloat(formData.rate),
      date: formData.date
    };

    if (editingRate) {
      updateCurrencyRate(editingRate.id, rateData);
    } else {
      addCurrencyRate(rateData);
    }
    resetForm();
  };

  const handleEdit = (rate: CurrencyRate) => {
    setEditingRate(rate);
    setFormData({
              currency: rate.currency as 'USD' | 'EUR' | 'CLF',
      rate: rate.rate.toString(),
      date: rate.date
    });
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta tasa de cambio?')) {
      deleteCurrencyRate(id);
    }
  };

  const resetForm = () => {
    setFormData({
      currency: 'USD',
      rate: '',
      date: ''
    });
    setEditingRate(null);
    setShowForm(false);
  };

  const formatCurrency = (rate: number, currency: string) => {
    if (currency === 'CLF') {
      return `$${rate.toLocaleString('es-CL', { minimumFractionDigits: 2 })} CLP`;
    } else {
      return `$${rate.toLocaleString('es-CL', { minimumFractionDigits: 2 })} CLP`;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const getCurrencyLabel = (currency: string) => {
    const currencyObj = currencies.find(c => c.code === currency);
    return currencyObj ? `${currencyObj.code} (${currencyObj.name})` : currency;
  };

  const getLatestRate = (currency: string) => {
    const rates = currencyRates
      .filter(r => r.currency === currency)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return rates[0];
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tasas de Cambio</h1>
          <p className="text-gray-600">Gestiona las tasas de conversión de divisas a CLP para {currentCompany?.name}</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nueva Tasa
        </button>
      </div>

      {/* Current Rates Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {currencies.map(currency => {
          const latestRate = getLatestRate(currency.code);
          return (
            <div key={currency.code} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg mr-3">
                    <DollarSign className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{currency.name}</h3>
                    <p className="text-sm text-gray-500">Tasa actual</p>
                  </div>
                </div>
                <div className="text-right">
                  {latestRate ? (
                    <>
                      <p className="text-lg font-bold text-green-600">
                        {formatCurrency(latestRate.rate, currency.code)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(latestRate.date)}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400">Sin datos</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingRate ? 'Editar Tasa' : 'Nueva Tasa de Cambio'}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Divisa *
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value as 'USD' | 'EUR' | 'CLF' })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {currencies.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tasa de Cambio (CLP) *
                </label>
                <input
                  type="number"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha *
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingRate ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por divisa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={currencyFilter}
              onChange={(e) => setCurrencyFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todas las divisas</option>
              {currencies.map(currency => (
                <option key={currency.code} value={currency.code}>
                  {currency.code} - {currency.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Rates List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Divisa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tasa (CLP)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredRates.length > 0 ? (
                filteredRates.map((rate) => (
                  <tr key={rate.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="p-2 bg-blue-100 rounded-lg mr-3">
                          <DollarSign className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {rate.currency}
                          </div>
                          <div className="text-sm text-gray-500">
                            {getCurrencyLabel(rate.currency)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(rate.rate, rate.currency)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        {formatDate(rate.date)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(rate)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(rate.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center">
                    <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No hay tasas de cambio</h3>
                    <p className="text-gray-600 mb-6">
                      {searchTerm || currencyFilter ? 'No se encontraron tasas con los filtros aplicados.' : 'Comienza agregando tu primera tasa de cambio.'}
                    </p>
                    {!searchTerm && !currencyFilter && (
                      <button
                        onClick={() => setShowForm(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                      >
                        Nueva Tasa
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CurrencyRatesList;